/**
 * ScreeningScorecard — single-line summary of the candidate's screening
 * answers against the recruiter's saved preferences.
 *
 * Two flavours:
 *   - Full (default): coloured pill row + a title, meant for the top of
 *     the candidate profile — hiring-manager glance widget.
 *   - Compact (`compact` prop): inline dots for the ScreeningProfileEditor
 *     header so recruiters see the roll-up right where they're editing.
 *
 * Both flavours are clickable. Clicking a pill dispatches a window-level
 * `screening:navigate` CustomEvent with `{ key }` — the editor listens
 * and opens the owning block, scrolls the field into view, and flashes
 * a subtle ring for context.
 *
 * Only counts fields where `prefs[key]` exists so the numbers stay
 * meaningful (fields with no pref config contribute nothing).
 */
import { CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { computeScreeningScorecard } from "@/lib/screening";

export default function ScreeningScorecard({ profile, prefs, fields, compact = false, className = "" }) {
    const stats = computeScreeningScorecard(profile, prefs, fields);
    // Nothing to show if the recruiter hasn't configured any preferences yet.
    if (!stats.configured) return null;

    const navigate = (statusKey) => {
        const key = stats.firstByStatus[statusKey];
        if (!key) return;
        window.dispatchEvent(new CustomEvent("screening:navigate", { detail: { key } }));
    };

    if (compact) {
        return (
            <div
                className={`inline-flex items-center gap-2 text-xs ${className}`}
                data-testid="screening-scorecard-compact"
            >
                <ScorecardPill
                    testid="scorecard-preferred-compact"
                    icon={<CheckCircle2 className="w-3 h-3" />}
                    color="emerald"
                    count={stats.preferred}
                    onClick={() => navigate("preferred")}
                    ariaLabel={`${stats.preferred} preferred answers`}
                    compact
                />
                <ScorecardPill
                    testid="scorecard-attention-compact"
                    icon={<AlertCircle className="w-3 h-3" />}
                    color="amber"
                    count={stats.needsAttention}
                    onClick={() => navigate("empty-important")}
                    ariaLabel={`${stats.needsAttention} important fields need attention`}
                    compact
                />
                <ScorecardPill
                    testid="scorecard-notpreferred-compact"
                    icon={<XCircle className="w-3 h-3" />}
                    color="rose"
                    count={stats.notPreferred}
                    onClick={() => navigate("not-preferred")}
                    ariaLabel={`${stats.notPreferred} non-preferred answers`}
                    compact
                />
            </div>
        );
    }

    return (
        <div
            className={`border border-border bg-white p-3 flex items-center gap-3 flex-wrap ${className}`}
            data-testid="screening-scorecard"
        >
            <div className="flex-1 min-w-[180px]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Screening scorecard</div>
                <div className="text-sm text-foreground/80 mt-0.5">
                    Roll-up of <span className="tabular-nums font-semibold">{stats.configured}</span> configured field{stats.configured === 1 ? "" : "s"}. Tap a pill to jump to the first match.
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <ScorecardPill
                    testid="scorecard-preferred"
                    icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                    color="emerald"
                    count={stats.preferred}
                    label="preferred"
                    onClick={() => navigate("preferred")}
                />
                <ScorecardPill
                    testid="scorecard-attention"
                    icon={<AlertCircle className="w-3.5 h-3.5" />}
                    color="amber"
                    count={stats.needsAttention}
                    label="need attention"
                    onClick={() => navigate("empty-important")}
                />
                <ScorecardPill
                    testid="scorecard-notpreferred"
                    icon={<XCircle className="w-3.5 h-3.5" />}
                    color="rose"
                    count={stats.notPreferred}
                    label="non-preferred"
                    onClick={() => navigate("not-preferred")}
                />
            </div>
        </div>
    );
}

const COLORS = {
    emerald: {
        active: "bg-emerald-50 border-emerald-400 text-emerald-800 hover:bg-emerald-100",
        muted:  "bg-white border-border text-muted-foreground",
    },
    amber: {
        active: "bg-amber-50 border-amber-400 text-amber-800 hover:bg-amber-100",
        muted:  "bg-white border-border text-muted-foreground",
    },
    rose: {
        active: "bg-rose-50 border-rose-400 text-rose-800 hover:bg-rose-100",
        muted:  "bg-white border-border text-muted-foreground",
    },
};

function ScorecardPill({ icon, color, count, label, onClick, testid, ariaLabel, compact }) {
    const styles = COLORS[color] || COLORS.emerald;
    const active = count > 0;
    const disabled = !active;
    return (
        <button
            type="button"
            onClick={active ? onClick : undefined}
            disabled={disabled}
            aria-label={ariaLabel || `${count} ${label || ""}`}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 border px-2 py-1 transition-colors ${active ? styles.active : styles.muted} ${disabled ? "opacity-50 cursor-default" : "cursor-pointer"} ${compact ? "text-xs h-6" : "text-sm h-8"}`}
        >
            {icon}
            <span className="tabular-nums font-semibold">{count}</span>
            {label && !compact && <span className="text-xs">{label}</span>}
        </button>
    );
}

/**
 * ScreeningScorecardStandalone — self-contained wrapper that fetches
 * screening fields catalog and per-company preferences on mount, then
 * renders the full scorecard using the passed-in `profile` snapshot.
 * Used at the top of the candidate profile Overview so hiring managers
 * see the roll-up before scrolling into Deep Screening.
 *
 * Fires no writes — clicking a pill still dispatches the
 * `screening:navigate` event which the editor picks up (if mounted).
 */
export function ScreeningScorecardStandalone({ profile, className }) {
    const [fields, setFields] = useState([]);
    const [prefs, setPrefs] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [fRes, pRes] = await Promise.all([
                    api.get("/screening-fields"),
                    api.get("/company/screening-prefs"),
                ]);
                if (!cancelled) {
                    setFields(fRes.data?.fields || []);
                    setPrefs(pRes.data?.screening_prefs || {});
                }
            } catch {
                if (!cancelled) setPrefs({});
            }
        })();
        return () => { cancelled = true; };
    }, []);
    if (prefs == null) return null; // still loading — no flash
    return (
        <ScreeningScorecard
            profile={profile || {}}
            prefs={prefs}
            fields={fields}
            className={className}
        />
    );
}

